import React, { useState, useRef, useEffect } from 'react';
import {
  ContentLayout, Header, Container, SpaceBetween,
  Button, Input, Box, Spinner, Alert, InputProps, Icon, IconProps,
} from '@amzn/awsui-components-react';
import { useAgentChat } from '../../hooks/useAgentChat';
import RawResponseView from '../Uat/RawResponseView';
import './AgentChat.scss';

/* ── Props ── */

export interface AgentChatProps {
  /** AgentCore runtime ARN to invoke */
  agentRuntimeArn: string;
  /** Header title */
  title: string;
  /** Header description line */
  description?: string;
  /** Input placeholder text */
  placeholder?: string;
  /** Cloudscape icon name for the header badge */
  headerIcon?: IconProps.Name;
  /** Empty-state heading */
  emptyHeading?: string;
  /** Empty-state subtitle */
  emptySubtitle?: string;
}

/* ── Component ── */

const AgentChat: React.FC<AgentChatProps> = ({
  agentRuntimeArn, title, description,
  placeholder = 'Type your message…',
  headerIcon = 'suggestions-gen-ai',
  emptyHeading = 'How can I help you today?',
  emptySubtitle,
}) => {
  const [input, setInput] = useState('');
  const { messages, streaming, error, sendMessage, reset } = useAgentChat(agentRuntimeArn);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || streaming) return;
    setInput('');
    sendMessage(trimmed);
  };

  const handleKeyDown: InputProps['onKeyDown'] = (e) => {
    if (e.detail.key === 'Enter' && !e.detail.shiftKey) handleSend();
  };

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          actions={<Button onClick={reset} disabled={streaming} iconName="refresh">New conversation</Button>}
          description={description}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <span className="icon-badge-sm">
              <Icon name={headerIcon} variant="inverted" />
            </span>
            {title}
          </span>
        </Header>
      }
    >
      <div style={{ width: '100%' }}>
        <SpaceBetween size="l">
          <Container>
            <div className="chat-area">
              {messages.length === 0 && !streaming && (
                <div className="empty-state">
                  <div className="icon-badge-lg">
                    <Icon name={headerIcon} variant="inverted" size="big" />
                  </div>
                  <Box variant="h3" color="text-body-secondary">{emptyHeading}</Box>
                  {emptySubtitle && <Box color="text-body-secondary" fontSize="body-s">{emptySubtitle}</Box>}
                </div>
              )}
              <SpaceBetween size="m">
                {messages.map((msg, i) => (
                  <div key={i} className={`message-row message-row--${msg.role}`}>
                    <div className={`message-content--${msg.role}`}>
                      {msg.role === 'user' ? (
                        <div className="user-bubble">{msg.content}</div>
                      ) : (
                        <div className="agent-bubble">
                          <RawResponseView content={msg.content || (streaming && i === messages.length - 1 ? '▍' : '')} />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {streaming && messages[messages.length - 1]?.role === 'assistant' && !messages[messages.length - 1]?.content && (
                  <div className="message-row message-row--assistant">
                    <div className="agent-bubble">
                      <Spinner size="normal" /> <span className="thinking">Thinking…</span>
                    </div>
                  </div>
                )}
              </SpaceBetween>
              <div ref={bottomRef} />
            </div>
          </Container>

          {error && <Alert type="error" dismissible onDismiss={() => reset()}>{error}</Alert>}

          <div className="input-bar">
            <div style={{ flex: 1 }}>
              <Input
                value={input}
                onChange={({ detail }) => setInput(detail.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                disabled={streaming}
              />
            </div>
            <Button variant="primary" onClick={handleSend} loading={streaming} disabled={!input.trim()} iconName="angle-right-double">
              Send
            </Button>
          </div>
        </SpaceBetween>
      </div>
    </ContentLayout>
  );
};

export default AgentChat;
